import React, { useState, useEffect } from 'react';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';

const PartnerRequestPage = () => {
  const [transfers, setTransfers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  useEffect(() => {
    fetchTransfers(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchTransfers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/transfer?page=${page}&limit=${limit}&search=${search}`);
      
      // Handle the response structure based on your API
      let transfersData = [];
      let totalCount = 0;
      
      if (res.data.data && Array.isArray(res.data.data)) {
        // Multiple transfers in data array
        transfersData = res.data.data;
        totalCount = res.data.totalCount || transfersData.length;
      } else if (res.data.data && !Array.isArray(res.data.data)) {
        // Single transfer object in data
        transfersData = [res.data.data];
        totalCount = 1;
      } else if (Array.isArray(res.data)) {
        // Direct array response
        transfersData = res.data;
        totalCount = res.data.length;
      } else {
        // Fallback
        transfersData = [];
        totalCount = 0;
      }

      console.log('Transfer data received:', transfersData); // Debug log to see the actual data structure
      
      setTransfers(transfersData);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error(err);
      toastError('Failed to fetch transfer requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (transferId, newStatus) => {
    if (window.confirm(`Are you sure you want to change status to ${newStatus}?`)) {
      try {
        const response = await axios.put(`/api/transfer/update`, {
          id: transferId,
          status: newStatus
        });
        
        if (response.data.Status === 1) {
          toastSuccess(response.data.Message || 'Status updated successfully!');
          fetchTransfers(currentPage, searchQuery);
        } else {
          toastError(response.data.Message || 'Failed to update status');
        }
      } catch (err) {
        console.error('Failed to update transfer status:', err);
        toastError(err.response?.data?.Message || 'Failed to update status');
      }
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Partner Transfer Requests</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Transfer Requests..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Transfer Requests List</h5>
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0" style={{ fontSize: '14px', lineHeight: '2' }}>
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">User Name</th>
                <th className="py-1 px-2">From Chapter</th>
                <th className="py-1 px-2">To Chapters</th>
                <th className="py-1 px-2">Transfer Date</th>
                <th className="py-1 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : transfers.length > 0 ? (
                transfers.map((transfer, idx) => (
                  <tr key={transfer._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">
                      {transfer.user && transfer.user.firstName && transfer.user.lastName ? (
                        <span>
                          {transfer.user.firstName} {transfer.user.lastName}
                        </span>
                      ) : transfer.user && (transfer.user.firstName || transfer.user.lastName) ? (
                        <span>
                          {transfer.user.firstName || ''} {transfer.user.lastName || ''}
                        </span>
                      ) : (
                        <span className="text-muted">Unknown User</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {transfer.oldChapter && transfer.oldChapter.length > 0 ? (
                        transfer.oldChapter.map((ch, i) => (
                          <span key={i} className="me-1 mb-1">
                            {ch.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {transfer.newchapter && transfer.newchapter.length > 0 ? (
                        transfer.newchapter.map((ch, i) => (
                          <span key={i} className="me-2 mb-1">{ch.name}</span>
                        ))
                      ) : <span className="text-muted"></span>}
                    </td>
                    <td className="py-1 px-2">{transfer.createdAt ? new Date(transfer.createdAt).toLocaleDateString() : '-'}</td>
                    <td className="py-1 px-2">
                      {transfer.status?.toLowerCase() === 'pending' ? (
                        <select 
                          className="form-select form-select-sm"
                          style={{ fontSize: '11px', padding: '2px 4px' }}
                          value={transfer.status}
                          onChange={(e) => handleStatusChange(transfer._id, e.target.value)}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      ) : (
                        <span 
                          className={`badge ${
                            transfer.status?.toLowerCase() === 'approved' ? 'bg-success' :
                            transfer.status?.toLowerCase() === 'rejected' ? 'bg-danger' :
                            'bg-warning'
                          }`} 
                          style={{ 
                            fontSize: '11px', 
                            padding: '4px 8px'
                          }}
                        >
                          {transfer.status ? transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1) : 'Pending'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-2">No Transfer Requests found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>&laquo;</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button key={page} className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #C44C23'
                  }}
                  onClick={() => setCurrentPage(page)}
                >{page}</button>
              ))}
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>&raquo;</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerRequestPage;
